import { Button, type ButtonProps } from "@/components/ui/Button";
import { cn } from "@/lib/cn";

/**
 * A button whose action is running: disabled, `aria-busy`, with a spinner over the label. The label
 * stays in place, transparent, so the button keeps its width and its accessible name.
 *
 * `keepFocus` is for the admin app: instead of a native `disabled`, which drops keyboard focus to the
 * page, the busy button stays focusable with `aria-disabled` and ignores presses (which also stops a
 * busy submit button from submitting its form again on Enter), shows the progress cursor, and under
 * reduced motion its ring breathes instead of turning. The front door (sign-in, consent, support code)
 * leaves it off, so nothing there changes for the people passing through it.
 */
export const BusyButton = ({
  busy,
  keepFocus = false,
  disabled,
  className,
  children,
  onClick,
  ...props
}: ButtonProps & { busy: boolean; keepFocus?: boolean }) => {
  const refusing = keepFocus && busy;
  return (
    <Button
      disabled={disabled || (busy && !keepFocus)}
      aria-busy={busy}
      aria-disabled={refusing || undefined}
      className={cn("relative", keepFocus && "aria-busy:cursor-progress", className)}
      onClick={(event) => {
        if (refusing) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      {...props}
    >
      <span className={cn(busy && "opacity-0")}>{children}</span>
      {busy && (
        <span
          aria-hidden="true"
          className={cn(
            "absolute size-5 animate-spin rounded-full border-2 border-current border-t-transparent",
            keepFocus && "motion-reduce:animate-pulse",
          )}
        />
      )}
    </Button>
  );
};
